import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import { getChecklistText } from "../helpers";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectCompanyInfo,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import AvatarCircle from "app/components/AvatarCircle";
import { selectCompanyGuide } from "app/containers/CompanyGuide/slice";
import { getPeopleOrPerson, getS } from "utils/helperFunctions";
import CircleEditButton from "app/storybookComponents/Button/CircleEditButton";
import Button from "app/storybookComponents/Button";
import {
  AdditionalFieldSelection,
  EmailManagementValidEmailType,
  EmailRecipientsPreview,
  ManagerScope,
  SendEmailOptions,
  SendToType,
} from "../types";

const MANAGER_SCOPE_LABEL_MAP: Record<ManagerScope, string> = {
  directReports: "direct reports",
  allSubordinates: "subordinates",
};

interface Props {
  sendToType: SendToType;
  ids: number[];
  onSave: () => void;
  onBack: () => void;
  onViewRecipients: () => void;
  recipientsPreview: EmailRecipientsPreview | null;
  isRecipientsPreviewLoading: boolean;
  hasRecipientsPreviewError: boolean;
  excludedUserAccountIds: number[];
  sendEmailOptions: SendEmailOptions;
  isLoading?: boolean;
  getWarningBanner?: (sendToType: string) => JSX.Element | null;
  confirmationDescriptionText?: string;
  additionalFieldSelection?: AdditionalFieldSelection | null;
  managerScope?: ManagerScope | null;
  emailType?: EmailManagementValidEmailType | null;
}

const SendEmailConfirmation = ({
  sendToType,
  ids,
  onSave,
  onBack,
  onViewRecipients,
  recipientsPreview,
  isRecipientsPreviewLoading,
  hasRecipientsPreviewError,
  excludedUserAccountIds,
  sendEmailOptions,
  isLoading,
  getWarningBanner,
  confirmationDescriptionText = "You’re sending an organization invite email to",
  additionalFieldSelection,
  managerScope,
  emailType,
}: Props) => {
  const users = useAppSelector(selectAllCompanyUsersById);
  const departments = useAppSelector(selectDepartments);
  const teams = useAppSelector(selectTeamsByTeamId);
  const currentCompanyGuide = useAppSelector(selectCompanyGuide);
  const companyInfo = useAppSelector(selectCompanyInfo);

  // "additionalField" sends criteria rather than ids, so who it covers is only known
  // once the API has resolved the preview.
  const additionalFieldRecipients = recipientsPreview?.recipients ?? [];
  const iconIds =
    sendToType === "additionalField"
      ? additionalFieldRecipients.map(({ userAccountId }) => userAccountId)
      : ids;

  const getIcons = () => {
    const hasRemaining = iconIds.length > 3;
    const splicedIds = iconIds.slice(0, 3);
    const iconMap: React.ReactNode[] = [];
    splicedIds.forEach((id) => {
      iconMap.push(getIcon(id));
    });
    if (hasRemaining) {
      iconMap.pop();
      const remainingCount = iconIds.length - 2;
      iconMap.push(<AvatarCircle name={`+ ${remainingCount}`} size="small" />);
    }

    return <div className="multi-avatar-container">{iconMap}</div>;
  };

  const generateSentToTextFromEntityNames = (stringArr: string[]) => {
    if (stringArr.length === 1) {
      return stringArr[0];
    }

    const hasRemaining = stringArr.length > 3;
    const splicedIds = stringArr.slice(0, 3);
    let text = "";
    splicedIds.forEach((id, idx) => {
      // if last element, don't add comma. add "and" instead
      // if hasRemaining, on the last element, add "and x more"
      const isLastElement = idx === splicedIds.length - 1;
      if (!isLastElement) {
        text += `${id}, `;
        return;
      }
      text += "and ";
      if (hasRemaining) {
        const remainingCount = stringArr.length - 2;
        const entityLabel =
          sendToType === "additionalField" ? "user" : sendToType.slice(0, -1);
        text += `${stringArr.length - 2} other ${entityLabel}${getS(
          remainingCount
        )}`;
        return;
      }
      text += id;
    });
    return text;
  };

  const getSentToText = () => {
    switch (sendToType) {
      case "organization": {
        return companyInfo?.companyName;
      }
      case "departments":
        return generateSentToTextFromEntityNames(
          ids.map((id) => departments[id]?.name ?? "")
        );
      case "teams":
        return generateSentToTextFromEntityNames(
          ids.map((id) => teams[id]?.teamName ?? "")
        );
      case "users":
      case "managers":
        return generateSentToTextFromEntityNames(
          ids.map((id) => {
            const user = users[id];
            return (
              `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
              user.emailAddress
            );
          })
        );
      case "additionalField":
        return generateSentToTextFromEntityNames(
          additionalFieldRecipients.map(({ firstName, lastName }) =>
            `${firstName ?? ""} ${lastName ?? ""}`.trim()
          )
        );
      default:
        return null;
    }
  };
  const getSentToDescriptionText = () => {
    const sendToOptionText = <b>{getChecklistText(sendEmailOptions)}</b>;

    switch (sendToType) {
      case "additionalField": {
        if (additionalFieldSelection) {
          return (
            <>
              Users where <b>{additionalFieldSelection.fieldName}</b> is{" "}
              <b>{additionalFieldSelection.fieldValue}</b>
            </>
          );
        }
        return `${additionalFieldRecipients.length} user${getS(
          additionalFieldRecipients.length
        )}`;
      }
      case "users": {
        return `${ids.length} user${getS(ids.length)}`;
      }
      case "managers": {
        // The managers named above are the pivot, not the audience, so this line has to say
        // whose inboxes it actually lands in.
        const scopeLabel =
          MANAGER_SCOPE_LABEL_MAP[managerScope ?? "directReports"];
        return (
          <>
            All <b>{scopeLabel}</b> of {ids.length} manager{getS(ids.length)}
          </>
        );
      }
      case "teams":
      case "departments": {
        const singularType = sendToType.slice(0, -1);
        if (ids.length === 1) {
          return (
            <>
              {sendToOptionText} in this {singularType}.
            </>
          );
        }
        return (
          <>
            {sendToOptionText} in these {ids.length} {singularType}
            {getS(ids.length)}.
          </>
        );
      }
      case "organization": {
        return <>{sendToOptionText} in this organization</>;
      }
      default:
        return null;
    }
  };

  const getIcon = (id: number) => {
    switch (sendToType) {
      case "users":
      case "managers":
      case "additionalField": {
        return <AvatarCircle userAccountId={id} size="small" key={id} />;
      }
      case "teams": {
        const teamInfo = teams[id];
        return (
          <AvatarCircle
            name={teamInfo?.teamName}
            size="small"
            avatarColor={teamInfo?.avatarColor}
            profilePicture={teamInfo?.profilePicture}
            key={id}
          />
        );
      }
      case "departments": {
        const departmentInfo = departments[id];
        return (
          <AvatarCircle name={departmentInfo?.name} size="small" key={id} />
        );
      }
      case "organization":
        return (
          <AvatarCircle
            name="Organization"
            size="small"
            profilePicture={currentCompanyGuide?.profilePicture?.picture}
            key={id}
          />
        );
      default:
        return null;
    }
  };

  const getRecipientCounts = () => {
    if (!recipientsPreview) {
      return { included: null, manuallyExcluded: 0, autoExcluded: 0 };
    }
    const excludedIdSet = new Set(excludedUserAccountIds);
    // Only count exclusions that apply to this preview — the selection can change
    // after someone is excluded, leaving stale ids that no longer match a recipient.
    const manuallyExcluded = recipientsPreview.recipients.filter((recipient) =>
      excludedIdSet.has(recipient.userAccountId)
    ).length;
    return {
      // "recipients" is already net of autoExcluded, so it is not subtracted here.
      included: recipientsPreview.recipients.length - manuallyExcluded,
      manuallyExcluded,
      autoExcluded: (recipientsPreview.autoExcluded ?? []).length,
    };
  };

  const {
    included: finalRecipientCount,
    manuallyExcluded: manuallyExcludedCount,
    autoExcluded: autoExcludedCount,
  } = getRecipientCounts();

  // Counts only those still being sent to - once excluded they are reported as excluded,
  // and saying "3 excluded, 3 emailed before" about the same three people reads as six.
  const previouslyEmailedCount = Object.keys(
    recipientsPreview?.eligibleRecipientsThatReceivedEmailsBefore ?? {}
  ).filter(
    (userAccountId) => !excludedUserAccountIds.includes(Number(userAccountId))
  ).length;

  const getEmailType = () => {
    if (!emailType) return "";
    switch (emailType) {
      case "TEAMScanInvite":
        return "TEAMscan invite";
      case "teamInvite":
        return "team invite";
      case "eppInvite":
        return "EPP";
      case "inviteToOrganization":
      default:
        return "invite";
    }
  };

  const getRecipientCountText = () => {
    if (isRecipientsPreviewLoading) {
      return "Calculating how many emails will be sent...";
    }
    if (hasRecipientsPreviewError) {
      return "We couldn’t work out who this would go to.";
    }
    if (finalRecipientCount === null) {
      return null;
    }
    const countText = `${finalRecipientCount} email${getS(
      finalRecipientCount
    )} will be sent`;
    const notes: string[] = [];
    if (manuallyExcludedCount) {
      notes.push(
        `${manuallyExcludedCount} ${getPeopleOrPerson(
          manuallyExcludedCount
        )} manually excluded`
      );
    }
    if (autoExcludedCount) {
      // The per-person reasons only live in the recipients list, which this line links to.
      notes.push(
        `${autoExcludedCount} ${getPeopleOrPerson(
          autoExcludedCount
        )} not eligible`
      );
    }

    if (previouslyEmailedCount) {
      notes.push(
        `${previouslyEmailedCount} ${getPeopleOrPerson(
          previouslyEmailedCount
        )} already received ${getEmailType()} email`
      );
    }
    if (!notes.length) {
      return `${countText}.`;
    }
    return `${countText} (${notes.join(", ")}).`;
  };

  return (
    <>
      <p>{confirmationDescriptionText}:</p>
      <div>
        <div className="row-gap-12px">
          {getIcons()}
          <div className="column-gap-4px">
            <p>
              <b>{getSentToText()}</b>
            </p>
            <p className="small-body-text grey-text">
              {getSentToDescriptionText()}
            </p>
          </div>
          <div className="ms-auto">
            <CircleEditButton onClick={onBack} />
          </div>
        </div>
      </div>
      <div className="column-gap-4px">
        {isRecipientsPreviewLoading ||
        hasRecipientsPreviewError ||
        finalRecipientCount === null ? (
          <p className="small-body-text grey-text">{getRecipientCountText()}</p>
        ) : (
          <Button variant="link" onClick={onViewRecipients}>
            {getRecipientCountText()}
          </Button>
        )}
      </div>
      {getWarningBanner?.(sendToType) ?? null}
      <SimpleModalFooter
        onBackButtonClick={onBack}
        onPrimaryButtonClick={onSave}
        primaryButtonText="Send Email"
        isPrimaryButtonDisabled={
          isLoading ||
          isRecipientsPreviewLoading ||
          hasRecipientsPreviewError ||
          finalRecipientCount === 0
        }
      />
    </>
  );
};

export default SendEmailConfirmation;
